export interface MonitoredOutputView {
  result: string;
  logs: string[];
}

const EMPTY: MonitoredOutputView = { result: '—', logs: [] };

export function mergeMonitoredLogs(...groups: string[][]): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const group of groups) {
    for (const line of group) {
      if (!line || seen.has(line)) {
        continue;
      }
      seen.add(line);
      lines.push(line);
    }
  }
  return lines.sort((left, right) => left.localeCompare(right));
}

export function extractMonitoredOutput(value?: string | null): MonitoredOutputView {
  if (!value) {
    return EMPTY;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      return {
        result: prettyValue(parsed),
        logs: readLogItems(parsed),
      };
    }
    if (!parsed || typeof parsed !== 'object') {
      return { result: prettyValue(parsed), logs: [] };
    }

    const root = parsed as Record<string, unknown>;
    const logs = readLogItems(root['Logs'] ?? root['logs'] ?? root['log']);
    const rest = { ...root };
    delete rest['Logs'];
    delete rest['logs'];
    delete rest['log'];
    return {
      result: Object.keys(rest).length > 0 ? prettyValue(rest) : '—',
      logs,
    };
  } catch {
    const lines = value
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    return lines.length > 1 ? { result: value, logs: lines } : { result: value, logs: [] };
  }
}

export function readStringProperty(source: object, ...names: string[]): string | null {
  const record = source as Record<string, unknown>;
  for (const name of names) {
    const value = record[name];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return null;
}

function readLogItems(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap(item => {
    if (typeof item === 'string' && item.length > 0) {
      return [item];
    }
    if (item && typeof item === 'object') {
      const message = readStringProperty(item, 'message', 'Message', 'text', 'Text');
      return message ? [message] : [];
    }
    return [];
  });
}

export function prettyJson(value?: string | null, empty = '—'): string {
  if (value == null || value === '') {
    return empty;
  }
  const prepared = stripJsonPrefix(value);
  const parsed = tryParseJson(prepared);
  if (parsed !== undefined) {
    return prettyValue(parsed);
  }
  const trimmed = prepared.trim();
  if (isHttpCapturePlaceholder(trimmed)) {
    return value;
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return prettyJsonLike(trimmed);
  }
  if (trimmed.startsWith('<')) {
    return prettyMarkupLike(trimmed);
  }
  return value;
}

function prettyValue(value: unknown): string {
  return JSON.stringify(expandNestedJson(value), null, 2);
}

function expandNestedJson(value: unknown, depth = 0): unknown {
  if (depth > 8) {
    return value;
  }
  if (typeof value === 'string') {
    const nested = tryParseJson(value);
    if (nested !== undefined && isJsonStructure(nested)) {
      return expandNestedJson(nested, depth + 1);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => expandNestedJson(item, depth + 1));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, expandNestedJson(nested, depth + 1)]),
    );
  }
  return value;
}

function tryParseJson(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return undefined;
  }
}

function stripJsonPrefix(value: string): string {
  return value.replace(/^\uFEFF/, '').replace(/^\)\]\}',?\s*/, '');
}

function isHttpCapturePlaceholder(value: string): boolean {
  return value === '[no body]'
    || value === '[no response]'
    || value.startsWith('[not stored:');
}

function prettyJsonLike(value: string): string {
  const indent = '  ';
  let output = '';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < value.length; index++) {
    const char = value[index];
    if (inString) {
      output += char;
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
      output += char;
      continue;
    }
    if (char === ' ' || char === '\n' || char === '\r' || char === '\t') {
      continue;
    }
    if (char === '{' || char === '[') {
      output += char;
      const next = nextNonWhitespace(value, index + 1);
      if (next !== '}' && next !== ']' && next !== '') {
        depth += 1;
        output += `\n${indent.repeat(depth)}`;
      }
      continue;
    }
    if (char === '}' || char === ']') {
      const previous = lastNonWhitespace(output);
      if (previous !== '{' && previous !== '[') {
        depth = Math.max(0, depth - 1);
        output += `\n${indent.repeat(depth)}`;
      }
      output += char;
      continue;
    }
    if (char === ',') {
      output += `,\n${indent.repeat(depth)}`;
      continue;
    }
    if (char === ':') {
      output += ': ';
      continue;
    }
    output += char;
  }

  return output;
}

function prettyMarkupLike(value: string): string {
  return value.replace(/>(\s*)</g, '>\n<');
}

function nextNonWhitespace(value: string, start: number): string {
  for (let index = start; index < value.length; index++) {
    const char = value[index];
    if (char !== ' ' && char !== '\n' && char !== '\r' && char !== '\t') {
      return char;
    }
  }
  return '';
}

function lastNonWhitespace(value: string): string {
  for (let index = value.length - 1; index >= 0; index--) {
    const char = value[index];
    if (char !== ' ' && char !== '\n' && char !== '\r' && char !== '\t') {
      return char;
    }
  }
  return '';
}

function isJsonStructure(value: unknown): boolean {
  return Array.isArray(value) || (!!value && typeof value === 'object');
}
